import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import SessionExpirationModal from './SessionExpirationModal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface AuthProviderProps {
  children: ReactNode;
}

function AuthProvider({ children }: AuthProviderProps) {
  const { isAuthenticated, isInitializing, initializeAuth, user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const requiresTwoFactor = useAuthStore(state => state.requiresTwoFactor);

  // Initialize auth state on mount only
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle authentication state changes
  useEffect(() => {
    if (!isInitializing) {
      // Define public paths that don't require authentication
      const publicPaths = ['/login', '/register', '/2fa-verify', '/fallback-response', '/fr', '/quick-copy', '/survey'];

      // Check if current path is public by checking if it starts with any of the public paths
      const isPublicPath = publicPaths.some(
        path => location.pathname === path || location.pathname.startsWith(`${path}/`)
      );

      if (!isAuthenticated && !isPublicPath && !requiresTwoFactor) {
        // Redirect to login if not authenticated and trying to access protected route
        navigate('/login', {
          replace: true,
          state: { from: location },
        });
      } else if (
        isAuthenticated &&
        (location.pathname === '/login' || location.pathname === '/register')
      ) {
        // Redirect to dashboard if authenticated and trying to access login or register
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, isInitializing, requiresTwoFactor, location, navigate]);

  // Log user information for debugging
  useEffect(() => {
    if (user) {
      console.log('Current user:', {
        id: user.id,
        username: user.username,
        isChildAccount: user.isChildAccount,
      });
    }
  }, [user]);

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <SessionExpirationModal />
      {children}
    </>
  );
}

export default AuthProvider;
